import { debounce } from 'lodash';
import React from 'react';
import { ChallengeStatus } from '@api/ChallengeClient';
import { userSearchDebounce } from '@components/hooks/use-search';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { SortingRule } from '@components/table';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';

export interface ChallengeDateRange {
    dateFrom?: string;
    dateTo?: string;
}

const DEFAULT_PAGE = {
    content: [],
    number: 0,
    size: 0,
    totalPages: 0,
    totalElements: 0
};

export function useChallengesStore() {
    const { challengeClient } = injectTSDI(Api);

    const [statusFilter, setStatusFilter] = React.useState<ChallengeStatus[]>(
        []
    );
    const [dateRange, setDateRange] = React.useState<ChallengeDateRange>({});
    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const challenges = useWrapRequest(
        async (
            params?: QueryParamsDto,
            statuses?: ChallengeStatus[],
            range?: ChallengeDateRange
        ) => {
            const result =
                await challengeClient.support.challengeSupportControllerGetPage(
                    {
                        ...params,
                        sort: params?.sort ?? '-ruleConfig.dateTimeRange.from',
                        statuses: statuses?.length ? statuses : undefined,
                        dateFrom: range?.dateFrom,
                        dateTo: range?.dateTo
                    }
                );

            return { ...result.data, content: result.data.content || [] };
        },
        { defaultData: DEFAULT_PAGE }
    );

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                params?: QueryParamsDto,
                statuses?: ChallengeStatus[],
                range?: ChallengeDateRange
            ) => {
                challenges.request([params, statuses, range]);
            },
            100
        ),
        []
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, statusFilter, dateRange);
    }, [searchParams, statusFilter, dateRange, debouncedAPICall]);

    const refresh = React.useCallback(() => {
        challenges.request([searchParams, statusFilter, dateRange]);
    }, [challenges, searchParams, statusFilter, dateRange]);

    return {
        challenges,
        searchParams,
        statusFilter,
        setStatusFilter,
        dateRange,
        setDateRange,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        refresh
    };
}
